import {
  faFileInvoice,
  faFileInvoiceDollar,
  type IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { faFileContract } from '@fortawesome/pro-regular-svg-icons/faFileContract'
import { faFileImage } from '@fortawesome/pro-regular-svg-icons/faFileImage'
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines'
import { faReceipt } from '@fortawesome/pro-regular-svg-icons/faReceipt'
import { type MantineColor } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreBookingDocumentCategory, type Maybe } from '@flecto/types'

type DocumentCategoryInfo = {
  icon: IconDefinition
  label: string
  value: CoreBookingDocumentCategory
  color: MantineColor
  category: CoreBookingDocumentCategory
}

type DocumentCategoryRaw = {
  id: CoreBookingDocumentCategory
  faIcon: IconDefinition
  color: MantineColor
}

/**
 * Returns document categories info, to be used on listings, etc.
 *
 * @usage
 *  const { categories, getCategory } = useBookingDocumentCategories()
 *  categories.map((cat) // => cat.label, cat.value, cat.color, cat.getIcon({ size: 'xs|sm...', withBackground: true|false }))
 *  getCategory('contract') // => { label, value, color, getIcon }
 *
 * @returns { categories, getCategory }
 */
export function useBookingDocumentCategories() {
  const { t } = useTranslation('shared', { keyPrefix: 'document_categories' })

  const rawCategories: DocumentCategoryRaw[] = [
    {
      id: 'contract',
      color: 'orange',
      faIcon: faFileContract,
    },
    {
      id: 'quote',
      color: 'purple',
      faIcon: faFileInvoice,
    },
    {
      id: 'proforma',
      color: 'blue',
      faIcon: faFileInvoiceDollar,
    },
    {
      id: 'image',
      color: 'blue',
      faIcon: faFileImage,
    },
    {
      id: 'invoice',
      color: 'brand',
      faIcon: faReceipt,
    },
    {
      id: 'others',
      color: 'cream',
      faIcon: faFileLines,
    },
  ]

  const categories = rawCategories.map((cat) => {
    return {
      icon: cat.faIcon,
      label: t(cat.id),
      value: cat.id,
      color: cat.color,
      category: cat.id,
    }
  })

  const getCategory = (
    category: CoreBookingDocumentCategory
  ): Maybe<DocumentCategoryInfo> => {
    return categories.find((cat) => cat.category === category)
  }

  return { categories, getCategory }
}
